'use strict';

const EARTH_RADIUS_METERS = 6371000;
const TWO_PI = Math.PI * 2;

const LOCATION_PRIVACY_VERSION = 2;
const DEFAULT_LOCATION_PRIVACY_RADIUS_METERS = 500;
const MINIMUM_OFFSET_RATIO = 0.55;
const MAXIMUM_OFFSET_RATIO = 0.85;
const OFFSET_TOLERANCE_RATIO = 0.05;

const isFiniteCoordinate = value => typeof value === 'number' && Number.isFinite(value);

const hasValidCoordinateLiteral = location =>
  isFiniteCoordinate(location?.lat) &&
  location.lat >= -90 &&
  location.lat <= 90 &&
  isFiniteCoordinate(location?.lng) &&
  location.lng >= -180 &&
  location.lng <= 180;

const coordinateDistanceMeters = (first, second) => {
  if (!hasValidCoordinateLiteral(first) || !hasValidCoordinateLiteral(second)) {
    return null;
  }

  const latitudeDelta = ((second.lat - first.lat) * Math.PI) / 180;
  const longitudeDelta = ((second.lng - first.lng) * Math.PI) / 180;
  const firstLatitude = (first.lat * Math.PI) / 180;
  const secondLatitude = (second.lat * Math.PI) / 180;
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(firstLatitude) * Math.cos(secondLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  const normalizedHaversine = Math.min(1, Math.max(0, haversine));

  return (
    EARTH_RADIUS_METERS *
    2 *
    Math.atan2(Math.sqrt(normalizedHaversine), Math.sqrt(1 - normalizedHaversine))
  );
};

const hasValidApproximateLocationContract = ({ geolocation, exactLocation, privacy }) => {
  const radiusMeters = Number(privacy?.radiusMeters);
  const hasCurrentMarker =
    Number(privacy?.version) >= LOCATION_PRIVACY_VERSION &&
    Number.isFinite(radiusMeters) &&
    radiusMeters > 0 &&
    hasValidCoordinateLiteral(geolocation);

  if (!hasCurrentMarker || !hasValidCoordinateLiteral(exactLocation)) {
    return hasCurrentMarker;
  }

  const distance = coordinateDistanceMeters(exactLocation, geolocation);
  return (
    distance >= radiusMeters * (MINIMUM_OFFSET_RATIO - OFFSET_TOLERANCE_RATIO) &&
    distance <= radiusMeters
  );
};

const createApproximateCoordinateLiteral = (
  exactCoordinates,
  radiusMeters,
  random = Math.random
) => {
  if (!hasValidCoordinateLiteral(exactCoordinates)) {
    throw new Error('Exact coordinates are required to create an approximate location.');
  }
  if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) {
    throw new Error('Location privacy radius must be greater than zero.');
  }

  const distanceRandom = random();
  const bearingRandom = random();
  const randomValuesAreValid =
    Number.isFinite(distanceRandom) &&
    distanceRandom >= 0 &&
    distanceRandom < 1 &&
    Number.isFinite(bearingRandom) &&
    bearingRandom >= 0 &&
    bearingRandom < 1;
  if (!randomValuesAreValid) {
    throw new Error('Location privacy random values must be between zero and one.');
  }

  const distanceRatio =
    MINIMUM_OFFSET_RATIO + distanceRandom * (MAXIMUM_OFFSET_RATIO - MINIMUM_OFFSET_RATIO);
  const distance = distanceRatio * radiusMeters;
  const bearing = bearingRandom * TWO_PI;
  const angularDistance = distance / EARTH_RADIUS_METERS;
  const latitude = (exactCoordinates.lat * Math.PI) / 180;
  const longitude = (exactCoordinates.lng * Math.PI) / 180;

  const approximateLatitude = Math.asin(
    Math.sin(latitude) * Math.cos(angularDistance) +
      Math.cos(latitude) * Math.sin(angularDistance) * Math.cos(bearing)
  );
  const approximateLongitude =
    longitude +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude),
      Math.cos(angularDistance) - Math.sin(latitude) * Math.sin(approximateLatitude)
    );
  const normalizedLongitude = ((approximateLongitude + Math.PI * 3) % TWO_PI) - Math.PI;

  return {
    lat: (approximateLatitude * 180) / Math.PI,
    lng: (normalizedLongitude * 180) / Math.PI,
  };
};

module.exports = {
  DEFAULT_LOCATION_PRIVACY_RADIUS_METERS,
  LOCATION_PRIVACY_VERSION,
  coordinateDistanceMeters,
  createApproximateCoordinateLiteral,
  hasValidApproximateLocationContract,
  hasValidCoordinateLiteral,
};
